import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import debounce from 'debounce';
import Point from './point';
import { isWithinViewport, isAndroid, isFirefox } from '../../../../utils';

export interface SparkleArgs {
  backgroundColor?: string;
  canvasElement: HTMLCanvasElement;
  parentContainer: HTMLElement;
  colorA: number[];
  colorB: number[];
  distanceThreshold: number;
  resolution: number;
  speedRange: number;
}

export default class Sparkle {
  backgroundColor?: string;
  canvasElement: HTMLCanvasElement;
  distanceThreshold: number;
  colorA: number[];
  colorB: number[];
  intervalId: number;
  numberOfPoints: number;
  parentContainer: HTMLElement;
  pointsArray: Point[];
  resolution: number;
  speedRange: number;

  // Three.js components
  renderer: THREE.WebGLRenderer;
  composer: EffectComposer;
  scene: THREE.Scene;
  camera: THREE.OrthographicCamera;
  mesh: THREE.Mesh;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  colors: Float32Array;
  alphas: Float32Array;

  static getNumberOfPoints(canvasElement: HTMLCanvasElement) {
    const throttle = 2500;
    const result = Math.max(
      (canvasElement.offsetWidth * canvasElement.offsetHeight) / throttle,
      75,
    );
    return result;
  }

  constructor(args: SparkleArgs) {
    this.canvasElement = args.canvasElement;
    if (!this.canvasElement) {
      // canvas is optional in some render paths; avoid noisy console output
    }

    this.numberOfPoints = Sparkle.getNumberOfPoints(this.canvasElement);
    this.colorA = args.colorA;
    this.colorB = args.colorB;
    this.distanceThreshold = args.distanceThreshold;
    this.parentContainer = args.parentContainer;
    this.resolution = args.resolution || 1;
    this.speedRange = args.speedRange || 1;
    this.intervalId = -1;
    this.pointsArray = [];
    this.backgroundColor = args.backgroundColor || '#222222';

    // Initialize Three.js
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvasElement,
      alpha: true,
      antialias: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.scene = new THREE.Scene();
    // this.scene.background = new THREE.Color(this.backgroundColor); // Use CSS for background

    // Camera setup
    const width = this.parentContainer.offsetWidth / this.resolution;
    const height = this.parentContainer.offsetHeight / this.resolution;
    this.camera = new THREE.OrthographicCamera(0, width, 0, height, 0.1, 1000);
    this.camera.position.z = 100;

    // Post-processing setup
    const renderScene = new RenderPass(this.scene, this.camera);
    const bloomPass = new UnrealBloomPass(new THREE.Vector2(width, height), 1.5, 0.4, 0.85);
    bloomPass.threshold = 0;
    bloomPass.strength = 1.5;
    bloomPass.radius = 0;

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(renderScene);
    this.composer.addPass(bloomPass);

    // Geometry setup
    const maxTriangles = 10000;
    this.geometry = new THREE.BufferGeometry();
    this.positions = new Float32Array(maxTriangles * 3 * 3);
    this.colors = new Float32Array(maxTriangles * 3 * 3);
    this.alphas = new Float32Array(maxTriangles * 3);

    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    this.geometry.setAttribute('alpha', new THREE.BufferAttribute(this.alphas, 1));

    // Material setup
    const material = new THREE.ShaderMaterial({
      vertexShader: `
        attribute float alpha;
        varying float vAlpha;
        varying vec3 vColor;
        void main() {
          vAlpha = alpha;
          vColor = color;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying float vAlpha;
        varying vec3 vColor;
        void main() {
          // Make it shine: if vAlpha is high, push color towards white
          float shine = smoothstep(0.5, 1.0, vAlpha);
          vec3 shinyColor = mix(vColor, vec3(1.0), shine);
          gl_FragColor = vec4(shinyColor, vAlpha);
        }
      `,
      transparent: true,
      vertexColors: true,
      side: THREE.DoubleSide,
      depthTest: false,
    });

    this.mesh = new THREE.Mesh(this.geometry, material);
    this.mesh.frustumCulled = false;
    this.scene.add(this.mesh);

    this.setSize();
    this.start();
    this.bindEvents();
  }

  bindEvents() {
    window.addEventListener('resize', debounce(this.setSize.bind(this), 100));
    window.addEventListener('scroll', () => {
      if (isAndroid() && isFirefox()) {
        this.stop();
        return;
      }

      if (!isWithinViewport(this.canvasElement)) {
        if (this.intervalId !== -1) {
          this.stop();
        }
      } else {
        if (this.intervalId === -1) {
          this.start();
        }
      }
    });

    window.addEventListener('scrollend', () => {
      if (isAndroid() && isFirefox()) {
        this.start();
        return;
      }
    });
  }

  animate() {
    const maxWidth = Number(this.canvasElement.width);
    const maxHeight = Number(this.canvasElement.height);
    const distanceThreshold = this.distanceThreshold;
    const distanceThresholdSq = distanceThreshold * distanceThreshold;

    // Spatial hashing
    const cellSize = distanceThreshold;
    const cols = Math.max(1, Math.ceil(maxWidth / cellSize));
    const rows = Math.max(1, Math.ceil(maxHeight / cellSize));
    const grid: Point[][] = new Array(cols * rows);
    for (let i = 0; i < grid.length; i++) grid[i] = [];

    const cellIndex = (x: number, y: number) => {
      const col = Math.max(0, Math.min(cols - 1, Math.floor(x / cellSize)));
      const row = Math.max(0, Math.min(rows - 1, Math.floor(y / cellSize)));
      return row * cols + col;
    };

    for (const p of this.pointsArray) {
      const idx = cellIndex(p.x, p.y);
      grid[idx].push(p);
    }

    let vertexIndex = 0;
    const positions = this.positions;
    const colors = this.colors;
    const alphas = this.alphas;

    const color1 = new THREE.Color('#ff00ff');
    const color2 = new THREE.Color('#0d58d3');

    for (let ai = 0; ai < this.pointsArray.length; ai++) {
      const pointA = this.pointsArray[ai];
      const colA = Math.max(0, Math.min(cols - 1, Math.floor(pointA.x / cellSize)));
      const rowA = Math.max(0, Math.min(rows - 1, Math.floor(pointA.y / cellSize)));

      const neighbors: Point[] = [];
      for (let r = Math.max(0, rowA - 1); r <= Math.min(rows - 1, rowA + 1); r++) {
        for (let c = Math.max(0, colA - 1); c <= Math.min(cols - 1, colA + 1); c++) {
          const bucket = grid[r * cols + c];
          for (const p of bucket) {
            if (p === pointA) continue;
            const dx = pointA.x - p.x;
            const dy = pointA.y - p.y;
            if (dx * dx + dy * dy <= distanceThresholdSq) {
              neighbors.push(p);
            }
          }
        }
      }

      for (let i = 0; i < neighbors.length; i++) {
        const pointB = neighbors[i];
        for (let j = i + 1; j < neighbors.length; j++) {
          const pointC = neighbors[j];

          const abx = pointA.x - pointB.x;
          const aby = pointA.y - pointB.y;
          const acx = pointA.x - pointC.x;
          const acy = pointA.y - pointC.y;
          const bcx = pointB.x - pointC.x;
          const bcy = pointB.y - pointC.y;

          const abSq = abx * abx + aby * aby;
          const acSq = acx * acx + acy * acy;
          const bcSq = bcx * bcx + bcy * bcy;

          if (
            abSq > distanceThresholdSq ||
            acSq > distanceThresholdSq ||
            bcSq > distanceThresholdSq
          )
            continue;

          const longestSq = Math.max(abSq, acSq, bcSq);
          const longest = Math.sqrt(longestSq);
          const intensity = 1 - longest / distanceThreshold;

          if (vertexIndex + 9 > positions.length) continue;

          const color = ai % 2 === 0 ? color1 : color2;

          // Vertex A
          positions[vertexIndex] = pointA.x;
          positions[vertexIndex + 1] = pointA.y;
          positions[vertexIndex + 2] = 0;
          colors[vertexIndex] = color.r;
          colors[vertexIndex + 1] = color.g;
          colors[vertexIndex + 2] = color.b;
          alphas[vertexIndex / 3] = intensity;
          vertexIndex += 3;

          // Vertex B
          positions[vertexIndex] = pointB.x;
          positions[vertexIndex + 1] = pointB.y;
          positions[vertexIndex + 2] = 0;
          colors[vertexIndex] = color.r;
          colors[vertexIndex + 1] = color.g;
          colors[vertexIndex + 2] = color.b;
          alphas[vertexIndex / 3] = intensity;
          vertexIndex += 3;

          // Vertex C
          positions[vertexIndex] = pointC.x;
          positions[vertexIndex + 1] = pointC.y;
          positions[vertexIndex + 2] = 0;
          colors[vertexIndex] = color.r;
          colors[vertexIndex + 1] = color.g;
          colors[vertexIndex + 2] = color.b;
          alphas[vertexIndex / 3] = intensity;
          vertexIndex += 3;
        }
      }

      // Update position
      pointA.x = (pointA.x + pointA.xSpeed) % (maxWidth + maxWidth * 0.1);
      pointA.y = (pointA.y + pointA.ySpeed) % (maxHeight + maxHeight * 0.1);

      if (pointA.x < maxWidth / -10) pointA.x = maxWidth;
      if (pointA.y < maxHeight / -10) pointA.y = maxHeight;
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.color.needsUpdate = true;
    this.geometry.attributes.alpha.needsUpdate = true;
    this.geometry.setDrawRange(0, vertexIndex / 3);

    this.composer.render();
  }

  setSize() {
    const width = this.parentContainer.offsetWidth / this.resolution;
    const height = this.parentContainer.offsetHeight / this.resolution;

    this.renderer.setSize(width, height, false);
    this.composer.setSize(width, height);
    this.canvasElement.width = width;
    this.canvasElement.height = height;
    this.renderer.setViewport(0, 0, width, height);

    this.camera.right = width;
    this.camera.bottom = height;
    this.camera.updateProjectionMatrix();

    this.numberOfPoints = Sparkle.getNumberOfPoints(this.canvasElement);
    this.resetPoints();
  }

  start() {
    this.intervalId = window.requestAnimationFrame(() => {
      this.animate();
      this.start();
    });
  }

  stop() {
    window.cancelAnimationFrame(this.intervalId);
    this.intervalId = -1;
  }

  resetPoints() {
    const isEven = () => Math.round(Math.random() * 10) % 2 === 0;
    const maxWidth = Number(this.parentContainer.offsetWidth);
    const maxHeight = Number(this.parentContainer.offsetHeight);

    this.pointsArray = [];

    for (let count = 0; count < this.numberOfPoints; count += 1) {
      const startX = (Math.random() * maxWidth) / this.resolution;
      const startY = (Math.random() * maxHeight) / this.resolution;
      const xSpeed = Math.random() * this.speedRange * (isEven() ? 1 : -1);
      const ySpeed = Math.random() * this.speedRange * (isEven() ? 1 : -1);

      this.pointsArray.push(new Point({ x: startX, y: startY, xSpeed: xSpeed, ySpeed: ySpeed }));
    }
  }
}
